import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { 
  obtenerClientes, 
  crearCliente, 
  actualizarCliente,
  eliminarCliente 
} from '../../services/clienteService';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Modal from '../../components/common/Modal';
import ClienteForm from '../../components/common/ClienteForm';
import NotificationModal from '../../components/common/NotificationModal';
import ConfirmActionModal from '../../components/common/ConfirmActionModal';
import BackButton from '../../components/common/BackButton';
import AdminLayout from '../../components/layout/AdminLayout';
import './ClientesAdmin.css';

const ClientesAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para notificaciones
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });
  
  // Estado para confirmación de eliminación
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    id: null,
    nombre: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarClientes();
  }, [user, navigate]);

  const showNotification = (title, message, type = 'success') => {
    setNotification({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeNotification = () => {
    setNotification({ ...notification, isOpen: false });
  };

  const cargarClientes = async () => {
    try {
      setLoading(true);
      const data = await obtenerClientes();
      setClientes(data);
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error', 'Error al cargar clientes: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async (clienteData) => {
    try {
      if (clienteEditando?.id) {
        await actualizarCliente(clienteEditando.id, clienteData);
        showNotification('Cliente Actualizado', 'El cliente ha sido actualizado exitosamente', 'success');
      } else {
        await crearCliente(clienteData);
        showNotification('Cliente Creado', 'El cliente ha sido creado exitosamente', 'success');
      }
      setModalOpen(false);
      cargarClientes();
    } catch (error) {
      showNotification('Error', 'Error al guardar: ' + error.message, 'error');
    }
  };

  const handleEliminarClick = (id, nombre) => {
    setConfirmDelete({
      isOpen: true,
      id: id,
      nombre: nombre
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await eliminarCliente(confirmDelete.id);
      showNotification('Cliente Eliminado', `El cliente ${confirmDelete.nombre} ha sido eliminado exitosamente`, 'success');
      setConfirmDelete({ isOpen: false, id: null, nombre: '' });
      cargarClientes();
    } catch (error) {
      showNotification('Error', 'Error al eliminar: ' + error.message, 'error');
      setConfirmDelete({ isOpen: false, id: null, nombre: '' });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ isOpen: false, id: null, nombre: '' });
  };

  // Activar/Desactivar acceso por pago
  const toggleAccesoPago = async (clienteId, nombre, estadoActual) => {
    const nuevoEstado = !estadoActual;
    const actionText = nuevoEstado ? 'activado' : 'desactivado';
    
    if (window.confirm(`¿Confirmar que el cliente ${nombre} ha realizado el pago? Esto le dará acceso a su espacio personal.`)) {
      try {
        const clienteRef = doc(db, 'clientes', clienteId);
        await updateDoc(clienteRef, { pagoCompletado: nuevoEstado });
        showNotification('Acceso Actualizado', `El acceso para ${nombre} ha sido ${actionText} exitosamente`, 'success');
        cargarClientes();
      } catch (error) {
        showNotification('Error', 'Error al actualizar acceso: ' + error.message, 'error');
      }
    }
  };

  const filtered = clientes.filter(c =>
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.documento?.includes(searchTerm) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <AdminLayout>
      <div className="clientes-admin-page">
        <div className="admin-navigation">
          <BackButton to="/dashboard" label="Volver al Dashboard" variant="outline" />
        </div>

        <div className="clientes-header">
          <h1>Gestión de Clientes</h1>
          <button onClick={() => { setClienteEditando(null); setModalOpen(true); }} className="btn-primary">
            + Nuevo Cliente
          </button>
        </div>

        <div className="clientes-filters">
          <input
            type="text"
            placeholder="Buscar por nombre, documento o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="clientes-grid">
          {filtered.length === 0 ? (
            <p className="no-results">No se encontraron clientes</p>
          ) : (
            filtered.map(c => (
              <div key={c.id} className="cliente-card">
                <div className="cliente-header">
                  <h3>{c.nombre}</h3>
                  <span className="cliente-documento">{c.tipoDocumento?.toUpperCase()}: {c.documento}</span>
                </div>
                <div className="cliente-info">
                  <p><strong>Email:</strong> {c.email}</p>
                  <p><strong>Teléfono:</strong> {c.telefono}</p>
                  {c.ciudad && <p><strong>Ciudad:</strong> {c.ciudad}</p>}
                </div>
                <div className="cliente-footer">
                  <div className="cliente-info-pago">
                    <span className={`pago-status ${c.pagoCompletado ? 'pagado' : 'pendiente'}`}>
                      {c.pagoCompletado ? 'Pago Completado' : 'Pago Pendiente'}
                    </span>
                    <span className="cliente-fecha">
                      Registrado: {c.fechaRegistro?.toLocaleDateString()}
                    </span>
                  </div>
                  <div className="cliente-actions">
                    {!c.pagoCompletado && (
                      <button 
                        onClick={() => toggleAccesoPago(c.id, c.nombre, c.pagoCompletado)}
                        className="btn-confirmar-pago"
                      >
                        Confirmar Pago
                      </button>
                    )}
                    {c.pagoCompletado && (
                      <button 
                        onClick={() => toggleAccesoPago(c.id, c.nombre, c.pagoCompletado)}
                        className="btn-desactivar-pago"
                      >
                        Revocar Acceso
                      </button>
                    )}
                    <button onClick={() => { setClienteEditando(c); setModalOpen(true); }} className="btn-edit">
                      Editar
                    </button>
                    <button onClick={() => handleEliminarClick(c.id, c.nombre)} className="btn-delete">
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={clienteEditando ? 'Editar Cliente' : 'Nuevo Cliente'}
        >
          <ClienteForm
            clienteInicial={clienteEditando || {}}
            onSubmit={handleGuardar}
            onCancel={() => setModalOpen(false)}
          />
        </Modal>

        {/* Notificación */}
        <NotificationModal
          isOpen={notification.isOpen}
          onClose={closeNotification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          autoClose={true}
        />

        {/* Modal de confirmación para eliminar */}
        <ConfirmActionModal
          isOpen={confirmDelete.isOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Confirmar eliminación"
          message={`¿Estás seguro de que deseas eliminar a "${confirmDelete.nombre}"? Esta acción no se puede deshacer.`}
          confirmText="Sí, eliminar"
          cancelText="Cancelar"
        />
      </div>
    </AdminLayout>
  );
};

export default ClientesAdmin;